/**
 * Per-flow video validation policies.
 *
 * Authoritative validation happens server-side via /services/probe-video,
 * which returns raw ffprobe data. The UI then applies one of these policies
 * via `validateVideoProbe` to decide whether the file may proceed.
 *
 * Adding a new policy: add a sibling object below and import it where the
 * relevant flow lives. Do not modify ZAPCAP_POLICY for one-off flow needs.
 */

export interface VideoPolicy {
    /** Lower-cased container tokens as returned by ffprobe (mp4, mov, webm, …). */
    allowedContainers: readonly string[];
    /** Inclusive max duration in seconds. */
    maxDurationS: number;
    /** Inclusive min duration in seconds. */
    minDurationS: number;
    /** Inclusive max file size in bytes. */
    maxBytes: number;
    /** Inclusive max for the larger of width or height. */
    maxDimensionPx: number;
    /** Inclusive min for the smaller of width or height. */
    minShortSidePx: number;
    /** Inclusive max frame rate. */
    maxFps: number;
}

/**
 * Policy for the ZapCap captioned-video flow.
 *
 * Limits and wording must stay in sync with `validateVideoProbe.ts` — the
 * messages there are authored to match these numbers.
 */
export const ZAPCAP_POLICY: VideoPolicy = {
    allowedContainers: ["mp4", "mov", "webm"],
    maxDurationS:      120,                   // 2 minutes
    minDurationS:      4,                     // 4 seconds
    maxBytes:          600 * 1024 * 1024,     // 600 MB
    maxDimensionPx:    1920,                  // Full HD ceiling
    minShortSidePx:    720,                   // HD minimum
    maxFps:            120,                   // matches ZapCap's documented export max
};
